import React, { useEffect, useState } from "react";
import { api } from "../../../lib/api";
import { ContentCards, CONTENT_PANELS } from "../ContentHub";
import { D, JAKARTA } from "../ui";
import { StatCard, ArrowBtn } from "./StatCard";
import { GrowthChart } from "./GrowthChart";
import { ArrowLeft, Eye, Users, BadgeCheck, Radio } from "lucide-react";

// ===== Dashboard — replika Gotrips (akses Medium Blue #0000CD menggantikan kuning); token D/JAKARTA dari components/admin/ui =====

const card = { background: "#fff", borderRadius: 26, padding: "20px 22px", position: "relative", boxShadow: "0 8px 22px rgba(20,20,20,0.05)" };
const cardTitle = { fontSize: 15, fontWeight: 800, color: D.ink };
const chip = (t, green = true) => (
  <span style={{ padding: "3px 10px", borderRadius: 99, fontSize: 11, fontWeight: 800, background: green ? D.chip : D.blueSoft, color: green ? D.chipInk : D.blue }}>{t}</span>
);
const byWarga = (a, b) => (b.warga - a.warga) || (b.buildings - a.buildings);

function CityActivity({ cities }) {
  return (
    <div data-testid="city-activity" style={{ ...card, flex: 1, minWidth: 300 }}>
      <b style={cardTitle}>Aktivitas Kota</b><ArrowBtn />
      <table style={{ width: "100%", marginTop: 14, borderCollapse: "collapse", fontSize: 12.5 }}>
        <thead>
          <tr style={{ color: D.sub, textAlign: "left", fontSize: 11 }}>
            <th style={{ padding: "4px 2px", fontWeight: 700 }}>Kota</th>
            <th style={{ padding: "4px 2px", fontWeight: 700 }}>Warga</th>
            <th style={{ padding: "4px 2px", fontWeight: 700 }}>Bangunan</th>
            <th style={{ padding: "4px 2px", fontWeight: 700, textAlign: "right" }}>Suka</th>
          </tr>
        </thead>
        <tbody>
          {[...cities].sort(byWarga).slice(0, 6).map(c => (
            <tr key={c.city_id} data-testid={`aktivitas-${c.city_id}`}>
              <td style={{ padding: "8px 2px", fontWeight: 700, textTransform: "capitalize" }}>{c.city_name}</td>
              <td style={{ padding: "8px 2px", fontWeight: 800, color: D.blue }}>{c.warga}</td>
              <td style={{ padding: "8px 2px", color: D.sub }}>{c.buildings}</td>
              <td style={{ padding: "8px 2px", textAlign: "right", fontWeight: 700 }}>{c.likes}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TopCities({ cities }) {
  // Kota Teramai = jumlah WARGA ASLI per kota (bukan bangunan)
  const top = [...cities].sort(byWarga).slice(0, 7);
  return (
    <div data-testid="top-cities" style={card}>
      <b style={cardTitle}>Kota Teramai</b><span style={{ display: "block", fontSize: 11, color: D.sub, fontWeight: 600, marginTop: 2 }}>berdasarkan jumlah warga asli</span><ArrowBtn />
      {top.map((c) => (
        <div key={c.city_id} data-testid={`teramai-${c.city_id}`} style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 14 }}>
          <span style={{ width: 38, height: 38, borderRadius: 12, background: D.blueSoft, display: "grid", placeItems: "center", flex: "none" }}>
            <Users size={17} color={D.blue} />
          </span>
          <span style={{ flex: 1, fontSize: 13, fontWeight: 700, textTransform: "capitalize" }}>{c.city_name}<span style={{ display: "block", fontSize: 11, color: D.sub, fontWeight: 600 }}>{c.buildings} bangunan</span></span>
          {chip(`${c.warga} warga`, c.warga > 0)}
        </div>
      ))}
    </div>
  );
}

export function DashboardPanel({ token, onTab }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState(null);       // null | radio | koran | info | pesan
  const [visits, setVisits] = useState(0);      // naik tiap kembali dari panel → ringkasan kartu dimuat ulang
  const [live, setLive] = useState(null);       // statistik langsung (warga online, pengunjung) — polling 10 dtk

  useEffect(() => {
    api.adminOverview(token).then(d => { setData(d); setLive(d.live); }).catch(e => setErr(e.message));
  }, [token]);
  useEffect(() => {
    const t = setInterval(() => api.adminLive(token).then(setLive).catch(() => {}), 10000);
    return () => clearInterval(t);
  }, [token]);

  function openPanel(key) {
    if (!key) setVisits(v => v + 1);
    setOpen(key);
  }

  if (err) return <p style={{ padding: 24, color: "#c00" }}>{err}</p>;
  if (!data) return <p style={{ padding: 24, color: D.sub }}>Memuat dashboard...</p>;

  // Panel konten terbuka "di tempat": kartu ringkasan menjadi pemilih di atas, panel penuh di bawahnya
  if (open) {
    const Panel = CONTENT_PANELS[open];
    return (
      <div data-testid="dashboard-panel" style={{ fontFamily: JAKARTA }}>
        <div style={{ padding: "22px 20px 0", display: "flex", flexDirection: "column", gap: 14 }}>
          <button data-testid="content-back" onClick={() => openPanel(null)}
            style={{ alignSelf: "flex-start", display: "inline-flex", alignItems: "center", gap: 7, padding: "8px 16px 8px 12px", borderRadius: 99, border: `1.5px solid ${D.line}`, background: "#fff", color: D.ink, fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: JAKARTA }}>
            <ArrowLeft size={14} /> Dashboard
          </button>
          <ContentCards token={token} open={open} onOpen={openPanel} compact refreshKey={visits} />
        </div>
        <div data-testid={`content-panel-${open}`}><Panel token={token} /></div>
      </div>
    );
  }

  const { cities, totals } = data;
  const L = live || data.live || {};
  const asliPct = L.warga_total ? Math.round((L.warga_asli / L.warga_total) * 100) : 0;
  const onlineNama = (L.online_daftar || []).slice(0, 3).map(w => `@${w.ig_username}`).join(", ");

  return (
    <div data-testid="dashboard-panel" style={{ padding: "22px 20px 30px", fontFamily: JAKARTA }}>
      <h2 style={{ margin: "0 0 18px", fontSize: 27, fontWeight: 800, letterSpacing: -0.6, color: D.ink }}>Dashboard</h2>
      <div style={{ display: "flex", gap: 18, alignItems: "flex-start" }}>
        {/* Kolom utama */}
        <div style={{ flex: 3, minWidth: 0, display: "flex", flexDirection: "column", gap: 18 }}>
          {/* 4 kartu statistik */}
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
            <StatCard testid="stat-online" icon={Radio} label="Warga Online" live value={L.online ?? 0} sub={onlineNama ? `${onlineNama}${(L.online || 0) > 3 ? ` +${L.online - 3}` : ""}` : "belum ada yang aktif · aktif ≤ 3 menit"} chipText={`${L.alun_online ?? 0} di alun-alun`} accent onOpen={() => onTab("warga")} />
            <StatCard testid="stat-warga" icon={Users} label="Total Warga Jawsel" value={L.warga_total ?? 0} sub="semua akun Kartu Warga" chipText={`+${L.warga_baru_hari_ini ?? 0} hari ini`} onOpen={() => onTab("warga")} />
            <StatCard testid="stat-asli" icon={BadgeCheck} label="Total Warga Asli" value={L.warga_asli ?? 0} sub="terverifikasi DM Instagram" chipText={`${asliPct}%`} onOpen={() => onTab("warga")} />
            <StatCard testid="stat-pengunjung" icon={Eye} label="Pengunjung Hari Ini" value={L.pengunjung_hari_ini ?? 0} sub="perangkat unik · hari WIB" chipText={`kemarin ${L.pengunjung_kemarin ?? 0}`} />
          </div>

          {/* Grafik + tabel aktivitas */}
          <div style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
            <GrowthChart cities={cities} totals={totals} card={card} cardTitle={cardTitle} />
            <CityActivity cities={cities} />
          </div>

          {/* Pusat konten: Radio, Koran, Info, Pesan Warga — klik untuk membuka panel penuh di tempat */}
          <ContentCards token={token} open={open} onOpen={openPanel} refreshKey={visits} />
        </div>

        {/* Sidebar kanan */}
        <div style={{ flex: 1, minWidth: 290, display: "flex", flexDirection: "column", gap: 18 }}>
          <TopCities cities={cities} />
        </div>
      </div>
    </div>
  );
}
